import type { I18nRecord } from '@/app/types';
// 固定刻度间距40px
export const SCALE_WIDTH = 40;
// 时间步长，默认为1（整数），设置为小于1的值允许小数位置
export const TIME_STEP = 1;
// 行高，默认为40px
export const ROW_HEIGHT = 40;

// 用户指南
export const T2A_USER_GUIDE_URL = {
  china: 'https://shengshu.feishu.cn/docx/QQ5Nd6WI1otNybxE9RjcAWnyn5d',
  global: 'https://important-pull-385.notion.site/AI-Sound-Effects-Quick-Start-Guide-1c8585802af0806cb23cfbe95f2bf393',
} as const;

export const T2A_LIMIT = {
  // 每段限制：500字
  SEGMENT_TEXT_LENGTH: 500,
  // 总字数限制：1000字
  TOTAL_TEXT_COUNT: 1000,
  // 时间戳间隔限制
  INTERVAL_LIMIT: 1,
  // 时间戳提示数限制
  TIMESTAMP_TIP_COUNT: 10,
  // 时间戳总数限制
  TIMESTAMP_COUNT: 15,
} as const;

export const T2A_FORM_BG = {
  china: 'https://image01-aliyun.vidu.zone/vidu/assets/t2a_form_bg.d3576e76.png',
  global: 'https://image01.cf.vidu.studio/vidu/assets/t2a_form_bg.d3576e76.png',
} as const;

// 示例
export const SAMPLES = {
  china: [
    {
      id: '1',
      segments: [
        {
          id: '1',
          text: '电脑键盘敲声',
          startTime: 0,
          endTime: 4,
        },
        {
          id: '2',
          text: '海浪拍打沙滩',
          startTime: 4,
          endTime: 6,
        },
      ],
      duration: 6,
    },
    {
      id: '2',
      segments: [
        {
          id: '1',
          text: '复古拉丁爵士，使用吉他、打击乐器、萨克斯和钢琴，节奏明快，适合热情的舞会或阳光午后的街头表演',
          startTime: 0,
          endTime: 0,
        },
      ],
      duration: 10,
    },
    {
      id: '3',
      segments: [
        {
          id: '1',
          text: '中国风纯音乐，使用古筝、笛子和柔和的打击乐器，展现古典山水意境',
          startTime: 0,
          endTime: 0,
        },
      ],
      duration: 10,
    },
  ],
  global: [
    {
      id: '1',
      segments: [
        {
          id: '1',
          text: 'typing keyboards',
          startTime: 0,
          endTime: 4,
        },
        {
          id: '2',
          text: 'ocean, big waves, crashing in on the beach, swells',
          startTime: 4,
          endTime: 6,
        },
      ],
      duration: 6,
    },
    {
      id: '2',
      segments: [
        {
          id: '1',
          text: 'a cat meows',
          startTime: 0,
          endTime: 2,
        },
        {
          id: '2',
          text: 'raindrops falling on the roof',
          startTime: 2,
          endTime: 7,
        },
      ],
      duration: 7,
    },
    {
      id: '3',
      segments: [
        {
          id: '1',
          text: 'Retro Latin Jazz with guitar, percussion, saxophone, and piano, with an upbeat rhythm, ideal for lively dance parties or sunny afternoon street performances',
          startTime: 0,
          endTime: 0,
        },
      ],
      duration: 10,
    },
  ],
};

// tips内容
const ZH_TIPS = [
  '试着想象自然、环境氛围、动作场景的声音～',
  '试试音乐bgm，有惊喜哦（不加时间戳效果更佳）',
  '声学环境和情绪描述词以增加沉浸感',
  '每个时间戳对应一段提示词',
  '1-3个时间戳叠加的效果最佳～',
  '建议单个声音的时长大于2s',
];
export const T2A_TIPS_TEXT: I18nRecord<string[]> = {
  china: {
    zh: ZH_TIPS,
  },
  global: {
    zh: ZH_TIPS,
    en: [
      'Describe natural, ambient, or action sounds',
      'Try BGM—no timestamps works best',
      'Add environment + emotion for immersion',
      'One prompt per timestamp',
      '1–3 timestamps work best',
      'Each sound >2s is ideal',
    ],
    ja: [
      '自然な環境音やアクション音を入れるとよりリアルに',
      'BGMもOK（タイムスタンプなしがいちばんスムーズ）',
      'シーンの雰囲気や感情を表現すると、もっと伝わる',
      'タイムスタンプごとにプロンプトは1つだけ',
      'タイムスタンプは1〜3個くらいがちょうどいい',
      '1つの音は2秒以上あると効果的',
    ],
    de: [
      'Beschreibe natürliche, Umgebungs- oder Actionsounds',
      'Probiere BGM – ganz ohne Zeitstempel funktioniert am besten',
      'Füge Umgebung & Emotion für mehr Atmosphäre hinzu',
      'Ein Prompt pro Zeitstempel',
      'Ideal: 1–3 Zeitstempel',
      'Jeder Sound sollte >2s dauern',
    ],
    es: [
      'Describe sonidos naturales, ambientales o de acción',
      'Prueba con música de ambiente. Ninguna marca de tiempo funciona mejor',
      'Añade entorno y emoción para lograr inmersión',
      'Un solo prompt por marca de tiempo',
      'De 1 a 3 marcas de tiempo funciona mejor',
      'Lo ideal es que cada sonido dure más de 2 segundos',
    ],
    fr: [
      "Décrivez des sons naturels, ambiants ou d'action.",
      'Essayez la musique de fond (BGM) — pas de timestamps, cela fonctionne mieux.',
      'Ajoutez environnement + émotion pour une immersion.',
      'Un prompt pour chaque timestamp.',
      '1 à 3 timestamps fonctionnent mieux.',
      'Chaque son >2s est idéal.',
    ],
    ko: [
      '자연음, 배경음, 액션음을 설명하세요',
      'BGM을 사용해 보세요. 타임스탬프 없이 사용하는 것이 가장 효과적입니다',
      '환경과 감정을 추가해 몰입감을 더해보세요',
      '타임스탬프마다 하나의 프롬프트를 사용하세요',
      '타임스탬프는 1~3개가 가장 효과적입니다',
      '각 사운드는 2초 이상이 이상적입니다',
    ],
    pt: [
      'Descreva sons naturais, ambientes ou de ação',
      'Experimente usar música de fundo — funciona melhor sem horários marcados',
      'Adicione ambiente e emoção para aumentar a imersão',
      'Um prompt por marcação de tempo',
      'De 1 a 3 marcações de tempo funcionam melhor',
      'Cada som com mais de 2 segundos é ideal',
    ],
    ru: [
      'Опишите естественные или фоновые звуки, либо звуки действия',
      'Попробуйте фоновую музыку — лучше без временных меток',
      'Добавьте окружение + эмоции для погружения',
      'Один промт на каждую временную метку',
      'Оптимально 1-3 временные метки',
      'Идеальная длительность каждого звука — более 2 секунд',
    ],
  },
};

// 轮换tips间隔（s）
export const T2A_TIPS_INTERVAL = 10;

// 建议提示词
export const T2A_SUGGESTIONS = {
  china: [
    '用笔在纸上写字',
    '倒水进玻璃杯声',
    '电风扇旋转',
    '人群欢呼鼓掌',
    '直升机螺旋桨旋转',
    '玻璃瓶摔碎',
    '水流拍打礁石',
  ],
  global: [
    'Writing on paper with a pen',
    'Pouring water into a glass',
    'Thunder',
    'Electric fan spinning',
    'Crowd cheering and clapping',
    'Helicopter rotor spinning',
    'Glass bottle shattering',
    'Birds chirping',
    'Water on reef rocks',
    'Typing keyboard',
    'Raindrops falling on the rooftop',
  ],
};
